import type { Namespace } from './types';
export const zh: Namespace = {
  notifications: {
    title: '通知',
    unreadCount: '{{count}} 条未读',
    countFailed: '未读数加载失败',
    loadMore: '加载更多',
    all: '全部',
    unreadOnly: '仅未读',
    markRead: '标为已读',
    markAllRead: '全部已读',
    allReadDone: '已全部标为已读',
    empty: '还没有通知',
    important: '重要',
    /** 主角行——浮层与通知列表共用的「账号：xxx」（标签行，2026-10-24 统一风格）。 */
    subjectAccount: '账号：{{name}}',
    /** CDKey 行（cdkey.bound 专属——完整卡密，收件人仅卡主）。 */
    cdkeyCode: 'CDKey：{{cdkey}}',
    /** 浮层风暴格多主角时的「几个账号」行。 */
    subjectsCount: '{{count}} 个账号',
    relative: {
      now: '刚刚',
      minutes: '{{count}} 分钟前',
      hours: '{{count}} 小时前',
      days: '{{count}} 天前',
    },
    relation: {
      waiter: '你发起的',
      anchor: '与你的资源相关',
      extra: '与你相关',
      related: '其他相关变更',
    },
    frame: {
      account_online: '账号已上线',
      account_need_code: '等待输入验证码',
      account_failed: '登录失败',
      redeem_card_processed: '兑换已处理',
      redeem_attribute: '兑换金额已回填',
      redeem_expired: '兑换已过期',
      cdkey_redeemed: 'CDKey 已兑换',
      grant_granted: '授权已生效',
      grant_revoked: '授权已撤销',
      order_account_deleted: '订单账号已删除',
      fx_refresh_failed: '汇率刷新失败',
      proxy_check_speed_done: '代理测速完成',
      broadcast: '系统公告',
      unknown: '未知事件',
    },
    /**
     * **浮层标题 = 事件名**（帧 + 分档派生，无插值）——用户裁定 2026-10-24：
     * 标题说「什么类型的事件」，主角（账号名）进正文药丸，不进标题。
     *
     * ⚠️ 与上面的 `frame.*` 是**两族**：`frame.*` 是通知列表的**类别标签**，
     * `toast.*` 是**浮层事件名**（按分档细分：等邮箱码 ≠ 等令牌码）——
     * 2026-10-24 起通知列表标题也用这一族（两面同一名）。
     */
    toast: {
      account_need_code_email: '等待邮箱验证码',
      account_need_code_token: '等待令牌验证码',
      account_need_code: '等待验证码',
      account_failed_login: '登录失败',
      account_failed_relogin: '重登失败',
      account_failed_verify: '验证失败',
      account_failed_token_refresh: '令牌刷新失败',
      account_failed_keep_alive: '保活失败',
      /** 兜底档：动作认不出（载荷没给 action）——不猜是哪类操作。 */
      account_failed: '操作失败',
      cdkey_bound: 'CDKey 已绑定',
    },
    stream: {
      idle: '未连接',
      open: '实时',
      connecting: '连接中',
      retrying: '重连中',
      offline: '已离线',
      degradedHint: '实时连接不可用，仅通知角标定时刷新',
      statusLabel: '事件流状态',
      lostHint: '实时连接已中断，正在重试；期间仅通知角标定时刷新',
      detailLabel: '诊断',
    },
    broadcast: {
      button: '发公告',
      title: '发布公告',
      importance: '重要性',
      importanceNormal: '普通（可被清理）',
      importanceImportant: '重要（免疫清理）',
      message: '公告正文',
      messageRequired: '请输入公告正文',
      target: '接收对象',
      targetAll: '全体用户',
      publish: '发布',
      published: '公告已发布',
    },
  },
};

export const en: Namespace = {
  notifications: {
    title: 'Notifications',
    unreadCount: '{{count}} unread',
    countFailed: 'Unread count failed to load',
    loadMore: 'Load more',
    all: 'All',
    unreadOnly: 'Unread',
    markRead: 'Mark read',
    markAllRead: 'Mark all read',
    allReadDone: 'All marked as read',
    empty: 'No notifications yet',
    important: 'Important',
    /** 主角行——浮层与通知列表共用的「账号：xxx」（标签行，2026-10-24 统一风格）。 */
    subjectAccount: 'Account: {{name}}',
    /** CDKey 行（cdkey.bound 专属——完整卡密，收件人仅卡主）。 */
    cdkeyCode: 'CDKey: {{cdkey}}',
    /** 浮层风暴格多主角时的「几个账号」行。 */
    subjectsCount: '{{count}} accounts',
    relative: {
      now: 'just now',
      minutes: '{{count}} min ago',
      hours: '{{count}} h ago',
      days: '{{count}} d ago',
    },
    relation: {
      waiter: 'Started by you',
      anchor: 'Involves your resources',
      extra: 'Related to you',
      related: 'Other related change',
    },
    frame: {
      account_online: 'Account online',
      account_need_code: 'Waiting for a code',
      account_failed: 'Login failed',
      redeem_card_processed: 'Redeem processed',
      redeem_attribute: 'Redeem amount backfilled',
      redeem_expired: 'Redeem expired',
      cdkey_redeemed: 'CDKey redeemed',
      grant_granted: 'Access granted',
      grant_revoked: 'Access revoked',
      order_account_deleted: 'Order account deleted',
      fx_refresh_failed: 'FX rate refresh failed',
      proxy_check_speed_done: 'Proxy speed check done',
      broadcast: 'Announcement',
      unknown: 'Unknown event',
    },
    /**
     * **浮层标题 = 事件名**（帧 + 分档派生，无插值）——用户裁定 2026-10-24：
     * 标题说「什么类型的事件」，主角（账号名）进正文药丸，不进标题。
     *
     * ⚠️ 与上面的 `frame.*` 是**两族**：`frame.*` 是通知列表的**类别标签**，
     * `toast.*` 是**浮层事件名**（按分档细分：等邮箱码 ≠ 等令牌码）——
     * 2026-10-24 起通知列表标题也用这一族（两面同一名）。
     */
    toast: {
      account_need_code_email: 'Waiting for email code',
      account_need_code_token: 'Waiting for 2FA code',
      account_need_code: 'Waiting for a code',
      account_failed_login: 'Login failed',
      account_failed_relogin: 'Re-login failed',
      account_failed_verify: 'Verification failed',
      account_failed_token_refresh: 'Token refresh failed',
      account_failed_keep_alive: 'Keep-alive failed',
      /** 兜底档：动作认不出（载荷没给 action）——不猜是哪类操作。 */
      account_failed: 'Operation failed',
      cdkey_bound: 'CDKey bound',
    },
    stream: {
      idle: 'Not connected',
      open: 'Live',
      connecting: 'Connecting',
      retrying: 'Reconnecting',
      offline: 'Offline',
      degradedHint: 'Live updates unavailable — only the notification badge refreshes on a timer',
      statusLabel: 'Event stream status',
      lostHint:
        'Live connection dropped, retrying — only the notification badge refreshes on a timer',
      detailLabel: 'Diagnostics',
    },
    broadcast: {
      button: 'Send announcement',
      title: 'Publish announcement',
      importance: 'Importance',
      importanceNormal: 'Normal (cleanable)',
      importanceImportant: 'Important (immune to cleanup)',
      message: 'Message',
      messageRequired: 'Please enter the announcement text',
      target: 'Recipients',
      targetAll: 'Everyone',
      publish: 'Publish',
      published: 'Announcement published',
    },
  },
};
